import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {isAlreadyNotified} from '@shared/api/services/v2/api-request';
import {SpinnerComponent} from '@shared/components/spinner/spinner.component';

/** A record that can be linked: only its id is required, the columns decide what is shown. */
export interface LinkCandidate {
    id: number;
}

/** One table column of the popup: the header text and how to read the cell value from a candidate. */
export interface LinkRecordsColumn<T extends LinkCandidate = LinkCandidate> {
    header: string;
    value: (candidate: T) => string | number | null | undefined;
}

/** Everything a linked-records tab supplies to configure the popup (assigned to its inputs). */
export interface LinkRecordsModalConfig<T extends LinkCandidate = LinkCandidate> {
    heading: string;
    titleId: string;
    columns: LinkRecordsColumn<T>[];
    loader: () => Promise<T[]>;
    submitLabel: string;
    successMessage: string;
    emptyMessage: string;
    submit: (ids: number[]) => Promise<void>;
}

/**
 * UNIVAPLAT-3694: configurable "link records" popup shared by the organisation tabs and the
 * contract-side Organisations tab. Cloned from LinkContractAdminsModal (which stays untouched).
 *
 * Opened by a linked-records tab via NgbModal with `ariaLabelledBy` set to `titleId`, which
 * supplies role=dialog, focus trap, Escape and focus return. Each candidate gets a checkbox;
 * the submit button calls `submit` once with every selected id and the popup closes with `true`.
 * On error it stays open with the selection kept so it can be retried.
 *
 * Backend owns the user-facing error text, shown verbatim unless the global interceptor already
 * toasted it. Candidate values (staff names, emails, reasons) are personal data: never logged,
 * and dropped when the popup is destroyed.
 */
@Component({
    selector: 'cdss-link-records-modal',
    standalone: true,
    imports: [SpinnerComponent],
    templateUrl: './link-records.modal.html'
})
export class LinkRecordsModal implements OnInit, OnDestroy, LinkRecordsModalConfig {
    @Input() heading = '';
    @Input() titleId = 'link-records-title';
    @Input() columns: LinkRecordsColumn[] = [];
    @Input() loader: () => Promise<LinkCandidate[]> = async () => [];
    @Input() submitLabel = 'Add selected';
    @Input() successMessage = 'Records linked';
    @Input() emptyMessage = 'Nothing left to link';
    @Input() submit: (ids: number[]) => Promise<void> = async () => undefined;

    candidates: LinkCandidate[] = [];
    loading = false;
    saving = false;
    loadError: string | null = null;

    private selected = new Set<number>();

    constructor(
        public modal: NgbActiveModal,
        private toastr: ToastrService,
    ) {
    }

    get selectedCount(): number {
        return this.selected.size;
    }

    async ngOnInit(): Promise<void> {
        this.loading = true;
        try {
            this.candidates = await this.loader();
        } catch (error: any) {
            this.loadError = error?.error?.error ?? 'The records could not be loaded.';
        } finally {
            this.loading = false;
        }
    }

    ngOnDestroy(): void {
        this.candidates = [];
        this.selected.clear();
    }

    isSelected(candidate: LinkCandidate): boolean {
        return this.selected.has(candidate.id);
    }

    toggle(candidate: LinkCandidate, checked: boolean): void {
        if (checked) {
            this.selected.add(candidate.id);
        } else {
            this.selected.delete(candidate.id);
        }
    }

    cell(column: LinkRecordsColumn, candidate: LinkCandidate): string {
        const value = column.value(candidate);
        return value === null || value === undefined ? '' : String(value);
    }

    /** Accessible name of a row's checkbox: its visible cell values. */
    rowName(candidate: LinkCandidate): string {
        return this.columns.map(column => this.cell(column, candidate)).filter(text => text !== '').join(' ');
    }

    async onSubmit(): Promise<void> {
        if (this.saving || this.loading || this.selected.size === 0) {
            return;
        }
        this.saving = true;
        try {
            await this.submit(Array.from(this.selected));
            this.toastr.success(this.successMessage, 'Success');
            this.modal.close(true);
        } catch (error: any) {
            // Modal stays open so the selection can be corrected and retried.
            if (!isAlreadyNotified(error)) {
                this.toastr.error(error?.error?.error);
            }
        } finally {
            this.saving = false;
        }
    }
}
